import { useState } from "react";
import { Shield, GitBranch, Wallet, ChevronRight, CheckCircle2 } from "lucide-react";

const layers = [
  {
    id: "governance",
    name: "Governance Layer",
    icon: Shield,
    color: "swarm-violet",
    description: "Policy engine and institutional controls",
    features: [
      "Policy Engine",
      "Approval Quorums",
      "Agent Identity & KYA",
      "Audit Trail",
      "Compliance Mapping"
    ],
    gradient: "from-swarm-violet/20 to-swarm-violet/5"
  },
  {
    id: "orchestration",
    name: "Orchestration Layer",
    icon: GitBranch,
    color: "swarm-cyan",
    description: "Workflow and agent management",
    features: [
      "Workflow Builder",
      "Agent Management",
      "Task Routing",
      "OpsControl Dashboard",
      "Real-time Monitoring"
    ],
    gradient: "from-swarm-cyan/20 to-swarm-cyan/5"
  },
  {
    id: "treasury",
    name: "Treasury Layer",
    icon: Wallet,
    color: "swarm-green",
    description: "Budget controls and revenue distribution",
    features: [
      "Distribution Mode",
      "Budget Control Mode",
      "Tap Rules & Velocity Caps",
      "Revenue Distribution Engine",
      "CFO Dashboard"
    ],
    gradient: "from-swarm-green/20 to-swarm-green/5"
  }
];

export const ArchitectureDiagram = () => {
  const [activeLayer, setActiveLayer] = useState(null);

  return (
    <div className="relative" data-testid="architecture-diagram">
      {/* Background glow */}
      <div className="absolute inset-0 bg-gradient-radial from-swarm-violet/5 via-transparent to-transparent opacity-50" />
      
      <div className="relative">
        {/* Connection lines - SVG */}
        <svg
          className="absolute left-1/2 top-0 -translate-x-1/2 h-full w-4 hidden md:block"
          viewBox="0 0 16 400"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="50%" stopColor="#06B6D4" />
              <stop offset="100%" stopColor="#10B981" />
            </linearGradient>
          </defs>
          <line
            x1="8"
            y1="0"
            x2="8"
            y2="400"
            stroke="url(#lineGradient)"
            strokeWidth="2"
            strokeDasharray="8 4"
            className="animate-pulse"
          />
        </svg>

        {/* Layers */}
        <div className="space-y-6 md:space-y-8">
          {layers.map((layer, index) => {
            const Icon = layer.icon;
            const isActive = activeLayer === layer.id;
            const colorClasses = {
              'swarm-violet': {
                bg: 'bg-swarm-violet/10',
                border: 'border-swarm-violet/30',
                text: 'text-swarm-violet',
                glow: 'shadow-[0_0_30px_rgba(139,92,246,0.3)]'
              },
              'swarm-cyan': {
                bg: 'bg-swarm-cyan/10',
                border: 'border-swarm-cyan/30',
                text: 'text-swarm-cyan',
                glow: 'shadow-[0_0_30px_rgba(6,182,212,0.3)]'
              },
              'swarm-green': {
                bg: 'bg-swarm-green/10',
                border: 'border-swarm-green/30',
                text: 'text-swarm-green',
                glow: 'shadow-[0_0_30px_rgba(16,185,129,0.3)]'
              }
            };
            const colors = colorClasses[layer.color];

            return (
              <div
                key={layer.id}
                className={`relative animate-slide-in-up`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <button
                  onClick={() => setActiveLayer(isActive ? null : layer.id)}
                  className={`w-full glass-card p-6 md:p-8 text-left transition-all duration-300 ${
                    isActive ? `${colors.border} ${colors.glow}` : 'border-swarm-border hover:border-slate-600'
                  }`}
                  data-testid={`architecture-layer-${layer.id}`}
                >
                  <div className="flex items-start gap-4 md:gap-6">
                    {/* Icon */}
                    <div className={`p-3 md:p-4 rounded-xl ${colors.bg} ${colors.text}`}>
                      <Icon className="w-6 h-6 md:w-8 md:h-8" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl md:text-2xl font-bold text-white">
                          {layer.name}
                        </h3>
                        <ChevronRight 
                          className={`w-5 h-5 text-slate-500 transition-transform duration-300 ${
                            isActive ? 'rotate-90' : ''
                          }`}
                        />
                      </div>
                      <p className="text-slate-400 text-sm md:text-base">
                        {layer.description}
                      </p>

                      {/* Expanded Features */}
                      <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 mt-6 overflow-hidden transition-all duration-500 ${
                        isActive ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        {layer.features.map((feature, fIndex) => (
                          <div
                            key={fIndex}
                            className="flex items-center gap-2 text-sm"
                          >
                            <CheckCircle2 className={`w-4 h-4 ${colors.text} flex-shrink-0`} />
                            <span className="text-slate-300">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ArchitectureDiagram;
